/**
 * FILE: apps/hub/src/components/PageHeader.tsx
 * PURPOSE: Reusable UI component.
 * AGENT_CONTEXT: Shared component. Keep <200 lines. Extract sub-components if needed.
 * LAST_REVIEWED: 2026-05-07
 */

import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

interface PageHeaderProps {
  title: string
  subtitle?: string
  actions?: ReactNode
  /** Optional area between title block and primary actions (filters, context tools). */
  contextSlot?: ReactNode
  /** Optional slot for route crumbs (TopBar also shows breadcrumbs globally). */
  breadcrumbs?: ReactNode
  className?: string
}

export function PageHeader({
  title,
  subtitle,
  actions,
  contextSlot,
  breadcrumbs,
  className,
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        'mb-6 flex flex-col gap-3 border-b border-border/60 pb-4 sm:flex-row sm:items-start sm:justify-between',
        className
      )}
    >
      <div className="min-w-0 space-y-1">
        {breadcrumbs ? <div className="mb-1">{breadcrumbs}</div> : null}
        <h1 className="font-heading text-xl font-semibold tracking-tight text-foreground">{title}</h1>
        {subtitle ? (
          <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
      <div className="flex min-w-0 flex-1 flex-col items-stretch gap-3 sm:items-end">
        {contextSlot ? (
          <div className="flex w-full flex-wrap justify-end gap-2 sm:w-auto">{contextSlot}</div>
        ) : null}
        {actions ? <div className="flex shrink-0 flex-wrap items-center justify-end gap-2">{actions}</div> : null}
      </div>
    </header>
  )
}
